import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { HiEye, HiEyeOff } from 'react-icons/hi';
import { useGeneral } from '../../contexts';
import Button from '../../components/ui/Button/Button';
import OTPInput from '../../components/ui/OTPInput/OTPInput';
import SEO from '../../components/SEO';
import formStyles from '../../components/ui/Form/Form.module.css';
import { getPublicToken } from '../../utils/publicToken';
import Services from '../../services/Services';
import styles from './ForgotPasswordPage.module.css';

interface PasswordValidation {
  reqLen: boolean;
  reqSpecial: boolean;
  reqNumber: boolean;
  reqUpper: boolean;
  reqLower: boolean;
  isValidPassword: boolean;
}

const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const { language, toastSuccess, toastError } = useGeneral();
  const { getTranslation } = language;

  const [email, setEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [enteredOTP, setEnteredOTP] = useState('');
  const [currentStep, setCurrentStep] = useState<0 | 1 | 2>(0);
  const [password, setPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  // Password validation
  const [passwordValidation, setPasswordValidation] = useState<PasswordValidation>({
    reqLen: false,
    reqSpecial: false,
    reqNumber: false,
    reqUpper: false,
    reqLower: false,
    isValidPassword: false
  });

  // Validate password in real-time
  useEffect(() => {
    const reqLen = password.length >= 8;
    const reqSpecial = /[^A-Za-z0-9ÇÖÜĞŞİçöüğşı]/.test(password);
    const reqNumber = /\d/.test(password);
    const reqUpper = /[A-ZÇĞİÖŞÜ]/.test(password);
    const reqLower = /[a-zçğıöşü]/.test(password);
    const isValidPassword = reqLen && reqSpecial && reqNumber && reqUpper && reqLower;
    
    setPasswordValidation({
      reqLen,
      reqSpecial,
      reqNumber,
      reqUpper,
      reqLower,
      isValidPassword
    });
  }, [password]);

  // Resend timer countdown
  useEffect(() => {
    let interval: NodeJS.Timeout | undefined;
    if (currentStep === 1 && resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [currentStep, resendTimer]);

  // Generate 6-digit OTP code
  const generateSixDigitCode = (): string => {
    return Math.floor(100000 + Math.random() * 900000).toString();
  };

  // Handle OTP input change
  const onChangeOTP = (otp: string[]) => {
    setEnteredOTP(otp.join(''));
  };

  // Validate email format
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Step 0: Send OTP
  const handleSendOTP = async () => {
    if (!email || !validateEmail(email)) {
      toastError(getTranslation('auth.emailAddress') + ' is invalid' || 'Please enter a valid email address.');
      return;
    }

    setIsLoading(true);
    try {
      const token = await getPublicToken();
      if (!token) {
        toastError(getTranslation('auth.systemNotReady') || 'System is not ready. Please refresh the page.');
        setIsLoading(false);
        return;
      }

      const otp = generateSixDigitCode();
      setOtpCode(otp);

      const response = await Services.SendOTPForResetPassword(token, otp, email);
      
      if (response.data.status) {
        setCurrentStep(1);
        setResendTimer(60);
        toastSuccess('OTP code has been sent to your email address.');
      } else {
        toastError(getTranslation('auth.otpNotSent') || 'OTP could not be sent. Please check your email address.');
      }
    } catch (error) {
      toastError(getTranslation('auth.otpNotSent') || 'OTP could not be sent. Please check your email address.');
    } finally {
      setIsLoading(false);
    }
  };

  // Step 1: Verify OTP
  const handleVerifyOTP = () => {
    if (enteredOTP.length !== 6) {
      toastError(getTranslation('auth.otpCodeIncorrect') || 'Please enter the complete 6-digit code.');
      return;
    }

    if (otpCode.toString() === enteredOTP.toString()) {
      setCurrentStep(2);
    } else {
      toastError(getTranslation('auth.otpCodeIncorrect') || 'OTP code is incorrect. Please check again.');
    }
  };

  // Step 2: Reset Password
  const handleResetPassword = async () => {
    if (!passwordValidation.isValidPassword) {
      toastError(getTranslation('auth.passwordRequirements') || 'Please meet the password requirements.');
      return;
    }

    if (password !== repeatPassword) {
      toastError(getTranslation('auth.passwordsDoNotMatch') || 'Passwords do not match.');
      return;
    }

    setIsLoading(true);
    try {
      const token = await getPublicToken();
      if (!token) {
        toastError(getTranslation('auth.systemNotReady') || 'System is not ready. Please refresh the page.');
        setIsLoading(false);
        return;
      }

      const response = await Services.ResetPassword(token, enteredOTP, email, password);
      
      if (response.data.status) {
        toastSuccess(getTranslation('auth.passwordResetSuccess') || 'Your password has been successfully changed. Please log in.');
        navigate('/login');
      } else {
        toastError(getTranslation('auth.passwordResetFailed') || 'Password reset failed. Please try again.');
      }
    } catch (error) {
      toastError(getTranslation('auth.passwordResetFailed') || 'Password reset failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle password change (remove spaces)
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\s/g, '');
    setPassword(value);
  };

  const handleRepeatPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\s/g, '');
    setRepeatPassword(value);
  };

  // Get button text based on current step
  const getButtonText = (): string => {
    if (isLoading) {
      switch (currentStep) {
        case 0:
          return getTranslation('auth.sending') || 'Sending...';
        case 1:
          return getTranslation('auth.verify') || 'Verifying...';
        case 2:
          return getTranslation('auth.resetting') || 'Resetting...';
        default:
          return '';
      }
    }
    switch (currentStep) {
      case 0:
        return getTranslation('auth.sendCode') || 'Send Code';
      case 1:
        return getTranslation('auth.verify') || 'Verify';
      case 2:
        return getTranslation('auth.resetPassword') || 'Reset Password';
      default:
        return '';
    }
  };

  // Check if button should be disabled
  const isButtonDisabled = (): boolean => {
    if (isLoading) return true;
    
    switch (currentStep) {
      case 0:
        return !email || !validateEmail(email);
      case 1:
        return enteredOTP.length !== 6;
      case 2:
        return !passwordValidation.isValidPassword || password !== repeatPassword;
      default:
        return true;
    }
  };

  const pageTitle = getTranslation('auth.forgotPasswordTitle') || 'Forgot Password';

  return (
    <>
      <SEO
        title={pageTitle}
        description="Reset your password"
        url={typeof window !== 'undefined' ? `${window.location.origin}/forgot-password` : undefined}
        noindex
        lang={language.language}
      />
      <div 
        className={styles.forgotPasswordPage}
        style={{
          backgroundImage: 'url(/form-background.jpg)'
        }}
      >
        <div className={styles.forgotPasswordContainer}>
          <div className={styles.brandSection}>
            <img 
              src="/lodos-favicon.png" 
              alt="Lodos Logo" 
              className={styles.logo}
            />
            <h1 className={styles.brandTitle}>ToGather</h1>
          </div>

          <div className={styles.welcomeSection}>
            <h2 className={styles.welcomeTitle}>{pageTitle}</h2>
            <p className={styles.welcomeSubtitle}>
              {currentStep === 0 && (getTranslation('auth.forgotPasswordSubtitleStep0') || 'Reset your password with the code that will be sent to your email address')}
              {currentStep === 1 && (getTranslation('auth.forgotPasswordSubtitleStep1') || 'Enter the 6-digit code sent to your email address')}
              {currentStep === 2 && (getTranslation('auth.forgotPasswordSubtitleStep2') || 'Set your new password')}
            </p>
          </div>

          {/* Step 0: Email Input */}
          {currentStep === 0 && (
            <form onSubmit={(e) => { e.preventDefault(); handleSendOTP(); }} className={formStyles.form}>
              <div className={formStyles.formField}>
                <label htmlFor="email" className={styles.label}>
                  {getTranslation('auth.emailAddress') || 'Email Address'}
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={styles.input}
                  placeholder={getTranslation('auth.email') || 'email@example.com'}
                  required
                  disabled={isLoading}
                />
              </div>

              <Button
                type="submit"
                variant="primary"
                size="large"
                disabled={isButtonDisabled()}
                className={styles.submitButton}
              >
                {getButtonText()}
              </Button>
            </form>
          )}

          {/* Step 1: OTP Verification */}
          {currentStep === 1 && (
            <form onSubmit={(e) => { e.preventDefault(); handleVerifyOTP(); }} className={formStyles.form}>
              <div className={formStyles.formField}>
                <label className={styles.label}>
                  {getTranslation('auth.sixDigitCode') || '6-Digit Code'}
                </label>
                <OTPInput onChangeOTP={onChangeOTP} disabled={isLoading} />
              </div>

              <div className={styles.resendRow}>
                <button
                  type="button"
                  className={styles.resendButton}
                  disabled={resendTimer > 0 || isLoading}
                  onClick={handleSendOTP}
                >
                  {getTranslation('auth.resendCode') || 'Resend Code'}
                </button>
                {resendTimer > 0 && (
                  <span className={styles.resendTimer}>
                    {resendTimer}s
                  </span>
                )}
              </div>

              <Button
                type="submit"
                variant="primary"
                size="large"
                disabled={isButtonDisabled()}
                className={styles.submitButton}
              >
                {getButtonText()}
              </Button>
            </form>
          )}

          {/* Step 2: Password Reset */}
          {currentStep === 2 && (
            <form onSubmit={(e) => { e.preventDefault(); handleResetPassword(); }} className={formStyles.form}>
              <div className={formStyles.formField}>
                <label htmlFor="password" className={styles.label}>
                  {getTranslation('auth.newPassword') || 'New Password'}
                </label>
                <div className={styles.passwordInputWrapper}>
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={handlePasswordChange}
                    className={styles.input}
                    placeholder="••••••••"
                    required
                    disabled={isLoading}
                    onKeyDown={(e) => {
                      if (e.key === ' ') {
                        e.preventDefault();
                        toastError(getTranslation('auth.passwordNoSpaces') || 'Password cannot contain spaces');
                      }
                    }}
                  />
                  <button
                    type="button"
                    className={styles.passwordToggle}
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={isLoading}
                  >
                    {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
                    {/* @ts-ignore - react-icons compatibility with React 19 */}
                    {showPassword ? <HiEyeOff size={20} /> : <HiEye size={20} />}
                  </button>
                </div>

                {password && (
                  <div className={styles.passwordRequirements}>
                    <div className={passwordValidation.reqLen ? styles.valid : styles.invalid}>
                      {getTranslation('auth.passwordMinLength') || '✓ At least 8 characters'}
                    </div>
                    <div className={passwordValidation.reqSpecial ? styles.valid : styles.invalid}>
                      {getTranslation('auth.passwordSpecialChar') || '✓ At least 1 special character'}
                    </div>
                    <div className={passwordValidation.reqNumber ? styles.valid : styles.invalid}>
                      {getTranslation('auth.passwordNumber') || '✓ At least 1 number'}
                    </div>
                    <div className={passwordValidation.reqUpper ? styles.valid : styles.invalid}>
                      {getTranslation('auth.passwordUppercase') || '✓ At least 1 uppercase letter'}
                    </div>
                    <div className={passwordValidation.reqLower ? styles.valid : styles.invalid}>
                      {getTranslation('auth.passwordLowercase') || '✓ At least 1 lowercase letter'}
                    </div>
                  </div>
                )}
              </div>

              <div className={formStyles.formField}>
                <label htmlFor="repeatPassword" className={styles.label}>
                  {getTranslation('auth.confirmPassword') || 'Confirm Password'}
                </label>
                <div className={styles.passwordInputWrapper}>
                  <input
                    id="repeatPassword"
                    type={showRepeatPassword ? 'text' : 'password'}
                    value={repeatPassword}
                    onChange={handleRepeatPasswordChange}
                    className={styles.input}
                    placeholder="••••••••"
                    required
                    disabled={isLoading}
                    onKeyDown={(e) => {
                      if (e.key === ' ') {
                        e.preventDefault();
                        toastError(getTranslation('auth.passwordNoSpaces') || 'Password cannot contain spaces');
                      }
                    }}
                  />
                  <button
                    type="button"
                    className={styles.passwordToggle}
                    onClick={() => setShowRepeatPassword(!showRepeatPassword)}
                    disabled={isLoading}
                  >
                    {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
                    {/* @ts-ignore - react-icons compatibility with React 19 */}
                    {showRepeatPassword ? <HiEyeOff size={20} /> : <HiEye size={20} />}
                  </button>
                </div>
                {repeatPassword && password !== repeatPassword && (
                  <span className={styles.errorText}>
                    {getTranslation('auth.passwordsDoNotMatch') || 'Passwords do not match'}
                  </span>
                )}
              </div>

              <Button
                type="submit"
                variant="primary"
                size="large"
                disabled={isButtonDisabled()}
                className={styles.submitButton}
              >
                {getButtonText()}
              </Button>
            </form>
          )}

          <div className={styles.links}>
            <Link to="/login" className={styles.link}>
              {getTranslation('auth.backToLogin') || '← Back to login page'}
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default ForgotPasswordPage;
